import type { OrganizationType } from '@/types/auth';

/**
 * What an organization TYPE shows, hides and renames — in one table.
 *
 * ⚠ UX ONLY, exactly like src/lib/capabilities.ts. pos-api is the boundary for
 * everything that matters; this module decides what the console PRESENTS. A
 * `true` here is never a permission, and hiding a screen is never access
 * control — the server re-answers every request regardless.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * The restaurant/ecommerce split was decided in at least six independent
 * shapes: `RequireRestaurant` in the router, `restaurantOnly`/`restaurantLabel`
 * flags in two nav tables, `resolveBusinessMode` in the queue, `isRestaurantType`
 * in the product editor, `showTables`/`showFloorPlan`/`isRestaurant` props on
 * the shells, and bare `=== 'restaurant'` string equality in the register and
 * the office product list. Adding a mode — or changing what one mode sees —
 * meant finding all of them, and the one that was missed failed silently in the
 * direction of showing a restaurant screen to a shop.
 *
 * This is that decision stated once, as data.
 *
 * ── THE TWO RULES ──────────────────────────────────────────────────────────
 *
 * 1. A NULL TYPE IS NEUTRAL AND IS NEVER GUESSED.
 *
 *    An absent type is the backend declining to say — the login payload's
 *    top-level `organizations[]` carries no `type` at all, and it survives only
 *    when the eager-loaded `user.organizations[]` relation carries it
 *    (src/types/auth.ts:114-142). 07-roles-permissions.md §4 requires that be
 *    read as "assume nothing", so neutral gets the INTERSECTION: no dine-in, no
 *    tables, and none of the retail-only screens either. Never the mode with
 *    the extra surfaces — that is the same rule src/features/auth/routing.ts
 *    already applies to the table routes.
 *
 * 2. THIS ANSWERS "WHICH FEATURES", NOT "WHICH LABELS ON A BOARD".
 *
 *    See `resolveBusinessMode` in src/features/pos/queue/stages.ts. That
 *    function answers a DIFFERENT question — which column headings to draw —
 *    and it deliberately falls back to the restaurant board for a null type,
 *    because a queue with no columns is not a safer queue. The two used to look
 *    like one question with two opposite answers. They are not the same
 *    question, and naming them apart is the point.
 */

/**
 * The user-facing mode.
 *
 * `ecommerce` presents as "Retail" throughout (00-product.md §Business modes):
 * the wire value is `ecommerce`, the word a merchant reads is Retail, and the
 * console must not mix them up in either direction.
 *
 * `quotation` maps to `neutral`: 03-pos-shell.md declares it unsupported and
 * 00-product.md requires an explicit product decision before launch. A neutral
 * console is the honest answer until that decision exists — it never claims a
 * shape the product has not agreed to.
 */
export type BusinessMode = 'restaurant' | 'retail' | 'neutral';

export type BusinessFeature =
  // ── Restaurant ───────────────────────────────────────────────────────────
  /** The floor plan, in both the office and the POS shell. */
  | 'tables'
  /** The dine-in order type, and everything the register hangs off it. */
  | 'dinein'
  /** Kitchen tickets. A shop has no pass to send one to. */
  | 'kot'
  /** Head count on a tab. */
  | 'covers'
  /** Appending a round to an open tab. */
  | 'rounds'
  /** Product add-on groups ("Extras") and the sold-as-add-on flag. */
  | 'addons'
  /** Per-product weekly availability hours. */
  | 'availability-schedule'
  /** Branch delivery radius + per-area charge (GET {pos}/area-branches). */
  | 'delivery-areas'
  /**
   * The COUNTER's catalog screen (`/pos/menu`) — search the catalog, flip
   * availability, correct stock by hand, without leaving the till.
   *
   * Restaurant-only, and the reason is the workflow rather than the data. That
   * screen is the "86 it" surface: the pass calls out that the biryani has run
   * out and whoever is nearest kills it from the till, in the middle of service,
   * because the next guest is already ordering it. It has to be one tap from the
   * register or it does not get used.
   *
   * A shop does not work that way. Availability follows stock, stock follows
   * receiving and counting, and both are back-office work — the console already
   * gives a retail merchant Products and Inventory for exactly this, with the
   * variant, warehouse and stock-movement context a till screen deliberately
   * does not carry. Putting a second, thinner catalog editor on the counter
   * offers the same job in two places and makes the weaker one the default.
   *
   * The CATALOG is not hidden — a retail cashier still searches and scans it
   * from the register, which is where selling happens. What goes is the counter
   * screen for EDITING it.
   */
  | 'counter-catalog'
  /**
   * The grid's "Top" shelf of featured products.
   *
   * It duplicates on purpose: a featured product renders under Top AND under
   * its own category, which is why the tiles are keyed `section:id`. On a menu
   * that is a "popular" shelf and reads as a shortcut. In a shop, scanning a
   * catalogue of distinct SKUs, the same product appearing twice reads as a
   * data fault and leaves each category count larger than the cards under it.
   */
  | 'featured-shelf'
  /**
   * Whether the merchant ever SEES a branch — the setup step, the switcher, the
   * branch settings screen.
   *
   * NOT whether the console sends X-Branch. It still does, in every mode. See
   * the note on `hasFeature` below: the day session cannot open without one.
   */
  | 'branch-ui'
  // ── Retail ───────────────────────────────────────────────────────────────
  /** Parcel shipping zones (src/features/office/shipping) — the retail model. */
  | 'shipping-zones'
  | 'brands'
  | 'warehouses'
  /** Storefront merchandising groups; ecommerce owns this surface. */
  | 'collections'
  /** Customer-facing website and its setup guide. */
  | 'online-store'
  /** Merchant-managed menu/operational categories, never ecommerce taxonomy. */
  | 'operational-categories'
  // ── Shared ───────────────────────────────────────────────────────────────
  | 'barcode'
  | 'variants'
  /** Day session / till open-close. */
  | 'shift';

/**
 * The verified type → mode. Strict equality against `organizations.type`, never
 * an inference from anything else: a waiter role does not make an organization
 * a restaurant, and a delivery area existing does not either.
 */
export function businessModeFor(type: OrganizationType | null | undefined): BusinessMode {
  if (type === 'restaurant') return 'restaurant';
  if (type === 'ecommerce') return 'retail';
  return 'neutral';
}

/**
 * feature → the modes that show it. Absent from the row = hidden in that mode.
 *
 * `branch-ui` is the one entry whose absence does NOT mean the concept is gone.
 * Retail hides the branch from the merchant, but the console keeps selecting one
 * and keeps sending the header, because pos-api's DaySessionController::start
 * validates `'branch_id' => 'required|exists:branches,id'`
 * (pos-api/app/Http/Controllers/Api/POS/DaySessionController.php:26) and
 * SessionStore.open refuses without one (src/stores/SessionStore.ts:194-195).
 * Dropping the header would take the shift, Today and the Z-report with it.
 * Making the branch genuinely optional is a BACKEND change; until it lands this
 * flag means "do not show it", and nothing more.
 */
const SHOWN_IN: Record<BusinessFeature, readonly BusinessMode[]> = {
  tables: ['restaurant'],
  dinein: ['restaurant'],
  kot: ['restaurant'],
  covers: ['restaurant'],
  rounds: ['restaurant'],
  // Neutral keeps both for the same reason as `counter-catalog` below: an
  // unstated type must not lose a surface it has always had.
  addons: ['restaurant', 'neutral'],
  'availability-schedule': ['restaurant', 'neutral'],
  // Neutral keeps delivery areas: `delivery` is offered in every mode and the
  // area is what PRICES it, so removing the picker would leave a delivery
  // nobody costed. Retail drops it because a shop prices parcels by zone.
  'delivery-areas': ['restaurant', 'neutral'],
  /*
    Restaurant AND neutral, not restaurant alone.

    Removing a screen from a tenant whose type the backend never stated would
    take away a surface that tenant has had all along, on nothing better than
    silence — Rule 1. A verified `ecommerce` organization is the only one that
    has actually said it manages its catalog from the office.
  */
  'counter-catalog': ['restaurant', 'neutral'],
  // Neutral keeps it for the same reason as `counter-catalog`: an unstated type
  // must not lose a section it has always had.
  'featured-shelf': ['restaurant', 'neutral'],
  'branch-ui': ['restaurant', 'neutral'],
  'shipping-zones': ['retail', 'neutral'],
  brands: ['retail', 'neutral'],
  warehouses: ['retail', 'neutral'],
  collections: ['retail'],
  'online-store': ['retail'],
  'operational-categories': ['restaurant', 'neutral'],
  barcode: ['restaurant', 'retail', 'neutral'],
  variants: ['restaurant', 'retail', 'neutral'],
  /*
    Every mode — and the office nav DISAGREES with that today.

    `/pos/today` and the day session behind it are gated on `sell`/`shift.open`
    and nothing else: pos-api/routes/api.php:178-186 carries no organization-type
    condition, so an ecommerce cashier opens and closes a till exactly as a
    restaurant one does. This row describes that.

    But the office's Sessions link is marked `restaurantOnly`
    (src/app/layouts/OfficeShell.tsx:252-257), so the same merchant can run a shift
    at the counter and then find no history of it in the back office.

    This row is NOT changed to match the nav, because the nav is the side that
    looks wrong — hiding the record of money that was genuinely taken is worse
    than showing an unused screen. It is left divergent, and loudly, because
    resolving it is an owner decision (docs/ECOMMERCE-POS-MODE-PLAN.md §6.2:
    "shift / day session in retail — on or off"), not a refactor's call. Nothing
    reads this row yet; the first consumer must not land before that answer does.
  */
  shift: ['restaurant', 'retail', 'neutral'],
};

/** Does this organization type present this feature? */
export function hasFeature(
  type: OrganizationType | null | undefined,
  feature: BusinessFeature,
): boolean {
  return SHOWN_IN[feature].includes(businessModeFor(type));
}

/**
 * Trade words that differ by mode.
 *
 * ONLY the wording decided ad hoc inside screens lives here. The two nav tables
 * keep their own `restaurantLabel` columns — a nav entry's label belongs beside
 * its route and its icon, and moving it here would split one row across two
 * files to save nothing.
 */
export type BusinessTerm =
  /**
   * The COUNTER's read-only catalog browser (`/pos/menu`, "Menu-lite").
   *
   * A shop reads "Catalog" here, not "Products", and the difference from
   * `catalog-admin` below is not pedantry — it is what the surrounding screen
   * already says. The POS rail's own tab for this route is labelled
   * 'Catalog'/'Menu' (src/app/layouts/PosShell.tsx:64-65,127-134), so a page
   * titled "Products" under a tab reading "Catalog" gives one place two names.
   */
  | 'catalog'
  /**
   * The OFFICE's catalog management list (`/products`).
   *
   * 'Products' for a shop, because the permanently-visible sidebar labels this
   * exact route 'Products' for every non-restaurant type
   * (src/app/layouts/OfficeShell.tsx:133-134), and the page's own "New product"
   * button, empty state and Product column all say so too. A title of "Catalog"
   * would sit in visible disagreement with the highlighted link that opened it.
   */
  | 'catalog-admin'
  /** One sellable row: an "Item" on a menu, a "Product" in a shop. */
  | 'item'
  /** The scannable code field. */
  | 'sku';

/*
  TWO CATALOG WORDS, NOT ONE — and the first draft of this table had one.

  It read `catalog: { restaurant: 'Menu', retail: 'Products', neutral: 'Catalog' }`
  and was pointed at BOTH the POS browser and the office list. Two independent
  readers hit the same wall from opposite sides: routing the POS page through it
  made a retail counter say "Products" under a rail tab saying "Catalog", and
  routing the office page through it made a retail back office say "Catalog"
  beside a sidebar link saying "Products".

  That is the tell. A single row cannot serve two screens whose NEIGHBOURING
  chrome already disagrees, and forcing one would have shipped a wording
  regression on whichever screen lost. The screens are different places with
  different furniture; they get different rows.

  Consequence worth stating: with the split, Phase 0 introduces ZERO user-visible
  wording change anywhere. Every cell below reproduces exactly what the screen
  rendered before the refactor.
*/
const WORDS: Record<BusinessTerm, Record<BusinessMode, string>> = {
  catalog: { restaurant: 'Menu', retail: 'Catalog', neutral: 'Catalog' },
  /*
    'Products' IN EVERY MODE (owner, 2026-08-20, twice).

    This answered 'Menu' for a restaurant and the instinct was right — it is
    the trade's own word. But `catalog-admin` titles the ADMIN screen at
    /products, and everything else on and around that screen says Product:
    the rail row that opens it, the New product button, the Product column,
    the empty state, the editor's breadcrumb. One title disagreeing with the
    five things surrounding it is worse than a slightly generic word.

    `catalog` above is untouched and still answers 'Menu': that one names
    what a DINER is looking at on the register, where the trade word is the
    only right one.
  */
  'catalog-admin': { restaurant: 'Products', retail: 'Products', neutral: 'Products' },
  item: { restaurant: 'Item', retail: 'Product', neutral: 'Product' },
  sku: { restaurant: 'Item code', retail: 'Stock code', neutral: 'Stock code' },
};

export function wordFor(type: OrganizationType | null | undefined, term: BusinessTerm): string {
  return WORDS[term][businessModeFor(type)];
}

/**
 * Convenience for the many callers that only ask the one question.
 *
 * Kept as a re-export shape rather than a second source of truth:
 * src/features/auth/routing.ts owns `isRestaurantType` because the ROUTING
 * policy is written in terms of it, and this module must agree with it by
 * construction rather than by two people remembering to.
 */
export function isRestaurantMode(type: OrganizationType | null | undefined): boolean {
  return businessModeFor(type) === 'restaurant';
}

/** True for `ecommerce` only — never for a null type. */
export function isRetailMode(type: OrganizationType | null | undefined): boolean {
  return businessModeFor(type) === 'retail';
}

/** Whether this organization's workstation cannot run without a branch. */
export function requiresBranch(type: OrganizationType | null | undefined): boolean {
  return !isRetailMode(type);
}
